import { useQueries } from '@tanstack/react-query'
import { YesObject } from '@yes.technology/react-toolkit'
import { RelationObjectProps } from 'component/TaskObjectCard/TaskObjectCard.types'
import { useMemo } from 'react'
import {
  AssociatedUsersRelationsKeys,
  AssociatedUsersRelationsProps
} from 'site/Renderer/components/Kanban/Kanban.types'
import fetchYesObjects, {
  FetchYesObjectsResponse
} from 'yesObject/shared/api/fetchYesObjects'

type UseAssociatedUsersProps = {
  taskUuid: string
  associatedUsersRelations: AssociatedUsersRelationsProps
  data: RelationObjectProps
}

type UuidToUrlMap = Record<string, string>

async function recursiveFetchRelations(
  data: YesObject[],
  projectFields: string[],
  fetchFunction: (
    uuid: string,
    objectclass: string,
    projectFields: Record<string, number>
  ) => Promise<FetchYesObjectsResponse>,
  parentToRootMap: Record<string, string> = {}
): Promise<UuidToUrlMap> {
  if (projectFields.length === 0) return {}

  const [currentField, ...remainingFields] = projectFields

  let combinedMap: UuidToUrlMap = {}

  for (const obj of data || []) {
    const objectKeys = Object.keys(obj)
    const currentRelationKey =
      objectKeys.length === 2
        ? objectKeys.filter((key) => key !== 'uuid')[0]
        : ''
    const currentFieldObject = obj?.[currentRelationKey]?.[0] as YesObject
    const uuid = currentFieldObject?.uuid ?? obj?.uuid
    const objectclass =
      currentFieldObject?.uuid_Objectclass ?? obj?.uuid_Objectclass

    const rootUuid = parentToRootMap[obj?.uuid] ?? obj?.uuid

    if (!uuid || !objectclass) continue

    const response = await fetchFunction(uuid, objectclass, {
      [currentField]: 1
    })

    const nestedData = response?.objects || []

    if (remainingFields.length === 0) {
      // Last level: map url by uuid
      nestedData.forEach((nestedObj) => {
        const urlField = nestedObj?.[currentField]?.[0] as YesObject
        combinedMap[rootUuid] = urlField?.des || ''
      })
    } else {
      const newParentToRootMap: Record<string, string> = {}
      for (const nd of nestedData) {
        newParentToRootMap[nd.uuid] = rootUuid
      }

      // Recurse deeper
      const childMap = await recursiveFetchRelations(
        nestedData,
        remainingFields,
        fetchFunction,
        newParentToRootMap
      )
      combinedMap = { ...combinedMap, ...childMap }
    }
  }

  return combinedMap
}

const fetchLevel = (
  uuid: string,
  objectclass: string,
  projectFields: Record<string, number>
) => {
  return fetchYesObjects({
    filterBody: {
      objectclass,
      orderby: [],
      pagination: { limit: 10 },
      query: {
        $and: [
          { 'uuid.des': { $eq: uuid } },
          { 'situation.des': { $gte: globalThis.activeSituationCode } }
        ]
      },
      skip_relations: false,
      skip_metadata: true,
      project_fields: projectFields
    },
    showLoading: false
  })
}

const useAssociatedUsers = ({
  taskUuid,
  associatedUsersRelations = [],
  data
}: UseAssociatedUsersProps) => {
  const { deferredAssociatedUsers, mappedRelations } = useMemo(() => {
    const relations =
      associatedUsersRelations?.map(({ relation }) => relation) || []

    const deferredAssociatedUsers = relations.reduce(
      (
        acc: Partial<Record<AssociatedUsersRelationsKeys, YesObject[]>>,
        relation: AssociatedUsersRelationsKeys
      ) => {
        const relationData = data?.[relation]
        if (
          relationData &&
          Array.isArray(relationData) &&
          relationData.length > 0
        ) {
          acc[relation] = relationData
        }
        return acc
      },
      {}
    )

    const mappedRelations = associatedUsersRelations.reduce(
      (
        acc: Partial<
          Record<
            AssociatedUsersRelationsKeys,
            {
              image: string
              readableName: string
            }
          >
        >,
        { image_path, readableName, relation }
      ) => {
        acc[relation] = { image: image_path || '', readableName }
        return acc
      },
      {}
    )

    return { deferredAssociatedUsers, mappedRelations }
  }, [associatedUsersRelations, data])

  const hasAssociatedUsers = Object.keys(deferredAssociatedUsers).length > 0

  const queries = Object.entries(deferredAssociatedUsers).flatMap(
    ([relation, relationData]) => {
      if (!relationData) return []

      const relationFields =
        mappedRelations?.[
          relation as AssociatedUsersRelationsKeys
        ]?.image.split('.') ?? []

      return [
        {
          queryKey: [taskUuid, 'associated-users', relation, ...relationFields],
          enabled: hasAssociatedUsers && relationFields.length > 0,
          queryFn: async () => {
            // Initial data for the relation
            const initialObjects = relationData
            // Fetch uuid-to-url map recursively
            return await recursiveFetchRelations(
              initialObjects,
              relationFields,
              fetchLevel
            )
          }
        }
      ]
    }
  )

  const resultData = useQueries({
    queries
  })

  const returnedData = useMemo(() => {
    const isLoading = resultData.some(({ isLoading }) => isLoading)
    const resultObject: Record<string, YesObject[]> = {}

    Object.entries(deferredAssociatedUsers).forEach(
      ([relationKey, objects]) => {
        const { readableName } =
          mappedRelations[relationKey as AssociatedUsersRelationsKeys] || {}
        const idx = Object.keys(deferredAssociatedUsers).indexOf(relationKey)
        const uuidToUrl = resultData[idx]?.data as UuidToUrlMap | undefined

        if (!objects || !readableName) return

        const enriched = objects.map((obj) => ({
          ...obj,
          url: uuidToUrl?.[obj.uuid] || ''
        }))

        resultObject[readableName] = Array.from(
          new Map(enriched.map((obj) => [obj.uuid, obj])).values()
        )
      }
    )

    return { isLoading, data: resultObject }
  }, [deferredAssociatedUsers, mappedRelations, resultData])

  return returnedData
}

export default useAssociatedUsers
